import React, { useEffect, useRef } from 'react';
import { formatDriveImage } from '../utils/menu';

// Temas y fuentes que elige el negocio en el panel (Novedades).
// ponytail: copia en management/src/components/AnnouncementPreview.js; si cambia uno, cambia el otro.
export const ANNOUNCEMENT_THEMES = {
  rojo: { card: 'linear-gradient(180deg, #860000 0%, #3a0000 100%)', text: '#ffffff', muted: '#fde8e8', accent: '#f4bd52', button: '#f4bd52', buttonText: '#3a0000' },
  crema: { card: '#fdf6e3', text: '#3b1d0f', muted: '#6b4a3a', accent: '#860000', button: '#860000', buttonText: '#ffffff' },
  verde: { card: 'linear-gradient(180deg, #166534 0%, #052e16 100%)', text: '#ffffff', muted: '#dcfce7', accent: '#fde68a', button: '#fde68a', buttonText: '#052e16' },
  noche: { card: '#111827', text: '#ffffff', muted: '#d1d5db', accent: '#f4bd52', button: '#f4bd52', buttonText: '#111827' },
};

export const ANNOUNCEMENT_FONTS = {
  clasica: { fontFamily: "'Archivo Black', sans-serif" },
  moderna: { fontFamily: "'Archivo', sans-serif", fontWeight: 800, letterSpacing: '-0.02em' },
  manuscrita: { fontFamily: "'Kaushan Script', cursive", fontWeight: 400 },
};

const MAX_PHOTOS = 6;

/** Fotos de la plantilla: las de los productos marcados y luego las extra. */
export const announcementPhotos = (a) => [
  ...(a.featured_items || []).filter((it) => it.img_item).map((it) => ({ src: it.img_item, alt: it.name })),
  ...(a.extra_images || []).map((src) => ({ src, alt: a.title || 'Novedad' })),
].slice(0, MAX_PHOTOS);

const gridCols = (n) => (n <= 1 ? 'grid-cols-1' : n === 2 || n === 4 ? 'grid-cols-2' : 'grid-cols-3');

const AnnouncementModal = ({ announcement, ctaLabel, onCta, onClose }) => {
  const ctaRef = useRef(null);

  useEffect(() => {
    if (!announcement) return undefined;
    ctaRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [announcement, onClose]);

  if (!announcement) return null;

  const theme = ANNOUNCEMENT_THEMES[announcement.theme] || ANNOUNCEMENT_THEMES.rojo;
  const font = ANNOUNCEMENT_FONTS[announcement.font] || ANNOUNCEMENT_FONTS.clasica;
  const isImage = announcement.mode === 'image';
  const photos = isImage ? [] : announcementPhotos(announcement);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/75 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-title"
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl border-2 border-white/80"
        style={{ background: theme.card, color: theme.text }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-2 right-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white transition-colors"
        >
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {isImage ? (
          <>
            <h2 id="announcement-title" className="sr-only">{announcement.title || 'Novedad'}</h2>
            <img
              src={formatDriveImage(announcement.image_url)}
              alt={announcement.title || 'Novedad de La Juanita'}
              className="block w-full h-auto"
            />
          </>
        ) : (
          <div className="px-5 pt-8 pb-2 sm:px-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: theme.accent }}>Novedad</p>
            <h2 id="announcement-title" className="mt-1 pr-10 text-3xl leading-tight" style={font}>
              {announcement.title}
            </h2>
            {announcement.body && (
              <p className="mt-3 text-base leading-relaxed whitespace-pre-line" style={{ color: theme.muted }}>
                {announcement.body}
              </p>
            )}
            {photos.length > 0 && (
              <ul className={`mt-5 grid gap-2 ${gridCols(photos.length)}`}>
                {photos.map((p) => (
                  <li key={p.src} className={`overflow-hidden rounded-xl bg-black/20 ${photos.length === 1 ? 'aspect-[4/3]' : 'aspect-square'}`}>
                    <img
                      src={formatDriveImage(p.src)}
                      alt={p.alt}
                      width="300"
                      height="300"
                      className="h-full w-full object-cover"
                      onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="p-5 sm:px-7">
          <button
            ref={ctaRef}
            type="button"
            onClick={onCta}
            className="h-12 w-full rounded-xl text-base font-extrabold shadow-lg cursor-pointer hover:brightness-110 active:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition"
            style={{ background: theme.button, color: theme.buttonText }}
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementModal;
